import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { sessionService } from "redux-react-session";

import BackdropLoader from "../../backdropLoader/BackdropLoader";
import { useSession } from "../../../hooks/useSession";
import CommonHeader from "../../CommonHeader";
import {
  callAPIInterface,
  displayPhoneNumber,
  fetchCountries,
  getCountryObj,
  mfaMethods,
} from "../../constants";
import {
  addNew,
  authenticatorMsg,
  disable,
  enable,
  loadingMsg,
  smsAuthenticationMsg,
  twoStepVerification,
} from "../../messages";
import AuthStatusCard from "./AuthStatusCard";
import TwoFactorDrawer from "./TwoFactorDrawer";
import GoogleAuthenticatorDrawer from "./GoogleAuthenticatorDrawer";

const Setup2FASettings = ({ isIosDevice }) => {
  const [openTwoFactorModal, setOpenTwoFactorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callingCode, setCallingCode] = useState("");
  const [smsAuthConfig, setSmsAuthConfig] = useState(null);

  const [gAuthConfig, setGAuthConfig] = useState(null);
  const [openGAuthModal, setOpenGAuthModal] = useState(false);
  const [accountCountryObj, setAccountCountryObj] = useState(null);
  const [gAuthSetup, setGAuthSetup] = useState(null);

  const { session } = useSession();

  const userPhoneNumber = session?.user?.phone_number;
  const userCallingCode = session?.user?.calling_code;
  const accountCountry = session?.account?.account?.country;

  const setMfaStatus = async () => {
    const currentSession = await sessionService.loadSession();

    try {
      let smsAuthConfig = null,
        gAuthConfig = null,
        isEnabled = false;
      setIsLoading(true);
      const mfaStatus = await callAPIInterface("GET", "/mfa");

      mfaStatus?.data?.forEach((mfaConfig) => {
        if (mfaConfig.mfa_type === "sms") {
          smsAuthConfig = mfaConfig;
        } else if (mfaConfig.mfa_type === "google_authenticator") {
          gAuthConfig = mfaConfig;
        }

        isEnabled =
          mfaConfig.is_google_auth_enabled ||
          mfaConfig.is_mfa_phone_number_verified;
      });

      setSmsAuthConfig(smsAuthConfig);
      setGAuthConfig(gAuthConfig);
      await sessionService.saveSession({
        ...currentSession,
        mfa_configurations: mfaStatus?.data,
        mfa_enabled: isEnabled,
      });
    } catch (e) {
      console.error("Error fetching MFA status", e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderGAuthStatus = () => {
    const isGAuthEnabled = gAuthConfig?.is_google_auth_enabled;
    const showGAuthCard =
      isGAuthEnabled || accountCountryObj?.is_google_auth_supported;

    const enabledActions = [
      {
        label: disable,
        variant: "contained",
        color: "error",
        onClick: () => {
          setOpenGAuthModal(true);
        },
      },
    ];

    const disabledActions = [
      {
        label: enable,
        variant: "contained",
        color: "primary",
        onClick: () => {
          getGAuthConfigurations();
        },
        loading: isFetching,
      },
    ];

    return (
      showGAuthCard && (
        <AuthStatusCard
          icon={mfaMethods["google_authenticator"].icon}
          title={mfaMethods["google_authenticator"].title}
          description={authenticatorMsg}
          actions={isGAuthEnabled ? enabledActions : disabledActions}
          isRecommended
        />
      )
    );
  };

  const renderSMSAuthStatus = () => {
    const isSmsAuthEnabled = smsAuthConfig?.is_mfa_phone_number_verified;
    const showSmsAuthCard =
      isSmsAuthEnabled || accountCountryObj?.is_twillio_supported;

    const enabledActions = [
      {
        label: disable,
        variant: "contained",
        color: "error",
        onClick: () => {
          setCallingCode(smsAuthConfig.mfa_calling_code);
          setPhoneNumber(smsAuthConfig.mfa_phone_number);
          setOpenTwoFactorModal(true);
        },
      },
    ];

    const disabledActionsWithPhone = [
      {
        label: addNew,
        variant: "outlined",
        color: "primary",
        onClick: () => {
          setCallingCode("");
          setPhoneNumber("");
          setOpenTwoFactorModal(true);
        },
      },
      {
        label: enable,
        variant: "contained",
        color: "primary",
        onClick: () => {
          setCallingCode(userCallingCode);
          setPhoneNumber(userPhoneNumber);
          setOpenTwoFactorModal(true);
        },
      },
    ];

    const disabledActionsWithoutPhone = [
      {
        label: enable,
        variant: "contained",
        color: "primary",
        onClick: () => {
          setCallingCode("");
          setPhoneNumber("");
          setOpenTwoFactorModal(true);
        },
      },
    ];

    const disabledActions = userPhoneNumber
      ? disabledActionsWithPhone
      : disabledActionsWithoutPhone;

    return (
      showSmsAuthCard && (
        <AuthStatusCard
          icon={mfaMethods["sms"].icon}
          title={mfaMethods["sms"].title}
          subtitle={
            isSmsAuthEnabled
              ? displayPhoneNumber(
                  smsAuthConfig.mfa_calling_code,
                  smsAuthConfig.mfa_phone_number
                )
              : userPhoneNumber
              ? displayPhoneNumber(userCallingCode, userPhoneNumber)
              : undefined
          }
          description={isSmsAuthEnabled ? null : smsAuthenticationMsg}
          actions={isSmsAuthEnabled ? enabledActions : disabledActions}
        />
      )
    );
  };

  const getGAuthConfigurations = async () => {
    setIsFetching(true);

    try {
      const mfaApiPath = "/mfa/google-auth";

      const res = await callAPIInterface("POST", mfaApiPath, {});

      await sessionService.saveSession({
        ...session,
        mfa_configurations: [res],
      });
      setGAuthSetup(res);
      setOpenGAuthModal(true);
    } catch (error) {
      setGAuthSetup(null);
    } finally {
      setIsFetching(false);
    }
  };

  const getMfaData = () => {
    setIsLoading(true);
    getAccountCountryObj()
      .then(async () => {
        await setMfaStatus();
      })
      .catch((err) => {
        console.error("Error getting MFA Data", err);
      })
      .finally(() => setIsLoading(false));
  };

  const getAccountCountryObj = async () => {
    try {
      const countries = await fetchCountries();
      const countryObj = getCountryObj("short_name", accountCountry, countries);
      setAccountCountryObj(countryObj);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (session) getMfaData();
  }, [session]);

  const twoFAModalProps = {
    openTwoFactorModal,
    setOpenTwoFactorModal,
    getMfaStatus: setMfaStatus,
    mfaData: smsAuthConfig,
    phoneNumber,
    callingCode,
    isIosDevice,
  };

  return (
    <>
      <BackdropLoader
        open={isLoading}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader textAfterIcon={twoStepVerification} icon="backArrow" />
      <Box className="options-container">
        <Box className="two-fa-setup-wrapper">
          <Box className="module-container">
            <Box gap={4} className="module-container-content">
              {renderGAuthStatus()}
              {renderSMSAuthStatus()}
            </Box>
          </Box>
        </Box>
      </Box>
      {openGAuthModal && (
        <GoogleAuthenticatorDrawer
          openModal={openGAuthModal}
          setOpenModal={setOpenGAuthModal}
          gAuthConfig={gAuthConfig}
          getMfaStatus={setMfaStatus}
          gAuthSetup={gAuthSetup}
        />
      )}
      {openTwoFactorModal && <TwoFactorDrawer {...twoFAModalProps} />}
    </>
  );
};
export default Setup2FASettings;
